import { plRouting } from '@onArte/shared/constants';
import { RouteNameEnum } from '@onArte/shared/enums';
import { RouteInfo } from '@onArte/shared/interfaces';

/**
 * Kategorie dzieł w adresach URL — Załącznik nr 1 poz. 13.
 *
 * Umowa wymienia dokładnie cztery kategorie z segmentem w adresie:
 * /dziela/malarstwo/…, /dziela/rzezba/…, /dziela/fotografia/…, /dziela/grafika/….
 * Dzieła pozostałych kategorii (rysunek, sztuka cyfrowa, design…) świadomie
 * zostają pod /dziela/[slug] — tak stanowi kryterium odbioru.
 *
 * Segment NIE pochodzi ze słownika `website_urls` (ten generuje core, którego
 * nie wdrażamy) — jest doklejany i zdejmowany po stronie website, po obu
 * stronach: na serwerze (SSR, przekierowania, mapa witryny) i w przeglądarce
 * (rozwiązywanie adresu w AppWrapperze).
 */

/** Nazwa kategorii z API (`items_categories.name`) → segment adresu. */
const categorySegmentsByName: ReadonlyMap<string, string> = new Map([
  ['painting', 'malarstwo'],
  ['sculpture', 'rzezba'],
  ['photography', 'fotografia'],
  ['graphics', 'grafika'],
]);

const categoryNamesBySegment: ReadonlyMap<string, string> = new Map(
  [...categorySegmentsByName.entries()].map(([name, segment]: [string, string]): [string, string] => [segment, name])
);

export function getArtworkCategorySegment(categoryName: string | null | undefined): string | null {
  return (categoryName && categorySegmentsByName.get(categoryName)) || null;
}

export function getArtworkCategoryNames(): string[] {
  return [...categorySegmentsByName.keys()];
}

/** Segmenty adresu: malarstwo, rzezba, fotografia, grafika. */
export function getArtworkCategorySegments(): string[] {
  return [...categorySegmentsByName.values()];
}

const artworkPathPrefix: string = '/dziela/';

/**
 * Czy trasa opisuje stronę dzieła — w obu kształtach adresu.
 *
 * Strona dzieła ma dwie trasy (z segmentem kategorii i bez), a widok jest jeden.
 * Każdy warunek na „to jest strona dzieła" MUSI iść przez tę funkcję: sprawdzanie
 * samego `RouteNameEnum.ArtworkDetails` wyłączało na adresach z kategorią pobieranie
 * danych, licytacje na żywo i obramowanie stopki.
 */
export function isArtworkDetailsRoute(routeName: RouteNameEnum | undefined): boolean {
  return routeName === RouteNameEnum.ArtworkDetails || routeName === RouteNameEnum.ArtworkDetailsInCategory;
}

/**
 * Trasa opisująca byt (adres rzeczywisty `/dziela/{id}`) — wzorzec bazowy także dla
 * żądań przychodzących z segmentem kategorii.
 */
export function resolveArtworkEntityRoute(route: RouteInfo): RouteInfo {
  return route.name === RouteNameEnum.ArtworkDetailsInCategory
    ? (plRouting.find((item: RouteInfo): boolean => item.name === RouteNameEnum.ArtworkDetails) ?? route)
    : route;
}

/** Czy segment adresu jest jedną z czterech kategorii z umowy. */
export function isKnownArtworkCategorySegment(segment: string): boolean {
  return categoryNamesBySegment.has(segment);
}

/**
 * Zdejmuje segment kategorii: `/dziela/malarstwo/slug` → `/dziela/slug`.
 * Adresy bez znanego segmentu wracają bez zmian — słownik ładnych adresów
 * zna wyłącznie formę bez kategorii.
 */
export function stripArtworkCategoryPath(pathname: string): string {
  if (!pathname.startsWith(artworkPathPrefix)) {
    return pathname;
  }

  const segments: string[] = pathname.slice(artworkPathPrefix.length).split('/');

  return (segments.length === 2 && categoryNamesBySegment.has(segments[0]))
    ? `${artworkPathPrefix}${segments[1]}`
    : pathname;
}

/**
 * Adres docelowy strony dzieła: z segmentem kategorii dla czterech kategorii
 * z umowy, bez segmentu dla pozostałych. To JEDYNE miejsce, które o tym
 * decyduje — canonical, przekierowania 301 i mapa witryny używają tej funkcji.
 */
export function buildArtworkCanonicalPath(prettyUrl: string, categoryName: string | null | undefined): string {
  const segment: string | null = getArtworkCategorySegment(categoryName);

  return (segment && prettyUrl.startsWith(artworkPathPrefix))
    ? `${artworkPathPrefix}${segment}/${prettyUrl.slice(artworkPathPrefix.length)}`
    : prettyUrl;
}
